import { useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Add, Button } from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { CellClickedEvent, ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useHistory } from 'react-router-dom';
import { checkEnableSurveyPresetsAndRecurrence } from 'utils/helpers/featureFlags';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { TextBodyMedium, TextHeader4, TextLabel } from 'uiToolkitV1/Typography';

import { useCreateNewSurveyForm } from './SurveyCreation/dataHook';
import { useGetSurveyList } from './dataHooks';
import { SURVEY_STATUS, SURVEY_STATUS_COLORS, SURVEY_STATUS_LABELS } from './types';

const SurveyStatusCellRenderer = ({ value }: { value: keyof typeof SURVEY_STATUS }) => {
  const color = SURVEY_STATUS_COLORS[value] ?? { bg: '#EBEFF3', text: '#333333' };

  return (
    <Box bg={color.bg} p="2px 8px" w="fit-content" borderRadius="4px">
      <TextBodyMedium color={color.text} bold>
        {SURVEY_STATUS_LABELS[value]}
      </TextBodyMedium>
    </Box>
  );
};

export const SurveyTab = () => {
  const { data: surveyList, isLoading: isSurveyListLoading, isError: isSurveyListError } = useGetSurveyList();
  const history = useHistory();
  const agGridRef = useRef<AgGridReact>(null);
  const { mutate: createNewSurveyForm, isLoading: isCreateNewSurveyFormLoading } = useCreateNewSurveyForm();

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { field: 'name', headerName: 'Name', flex: 2 },
      {
        field: 'currentStage',
        headerName: 'Status',
        flex: 1,
        filter: 'agSetColumnFilter',
        cellRenderer: SurveyStatusCellRenderer,
        filterParams: {
          valueFormatter: (params: { value: keyof typeof SURVEY_STATUS | string }) =>
            SURVEY_STATUS_LABELS[params.value as keyof typeof SURVEY_STATUS] || params.value,
        },
      },
    ],
    []
  );

  const rowData = useMemo(() => {
    if (!surveyList || isSurveyListLoading || isSurveyListError) return [];
    return surveyList;
  }, [surveyList, isSurveyListLoading, isSurveyListError]);

  const handleCellClick = (event: CellClickedEvent) => {
    const surveyId = event.data.id;
    if (!surveyId) return;
    // navigate to survey detail page
    const url = L3_PATHS.company.engagement.surveyCreation.replace(':surveyId', surveyId);
    history.push(url);
  };

  const handleAddNewSurvey = () => {
    if (checkEnableSurveyPresetsAndRecurrence()) {
      history.push(L3_PATHS.company.engagement.surveyCreation.replace(':surveyId', 'presets'));
      return;
    }
    createNewSurveyForm();
  };

  return (
    <Flex flexDir="column" p="12px" gap="12px">
      {/* Header */}
      <Flex justifyContent="space-between" p="4px 8px">
        <Box>
          <TextHeader4 bold>Survey</TextHeader4>
          <TextLabel>Set up engagement survey(s) for your organization.</TextLabel>
        </Box>
        <Button
          iconLeft={Add}
          variant="outline"
          style={{ height: '32px', whiteSpace: 'nowrap' }}
          onClick={handleAddNewSurvey}
          disabled={isCreateNewSurveyFormLoading}
        >
          Add new
        </Button>
      </Flex>
      <Box w="100%">
        <AgGridTable
          // @ts-expect-error ignore
          ref={agGridRef}
          defaultColDef={{
            resizable: false,
            autoHeight: true,
            headerComponentParams: { enableMenu: true, enableSorting: true },
            menuTabs: ['filterMenuTab'],
            headerComponent: AgGridHeader,
            filter: 'agTextColumnFilter',
            cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
          }}
          columnDefs={columnDefs}
          rowData={rowData}
          domLayout="autoHeight"
          kebabMenuIsRowSpecific={false}
          containerAutoHeight
          pagination
          headerHeight={40}
          suppressPaginationPanel={!rowData?.length || rowData.length < 10}
          paginationPageSize={10}
          showSidebarToggle={false}
          onCellClicked={handleCellClick}
          loading={isSurveyListLoading}
        />
      </Box>
    </Flex>
  );
};
